import { useState, useEffect, useRef, memo, useMemo } from "react";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableOptions,
  type MRT_Updater,
} from "mantine-react-table";
import { PaginationMetadata } from "constants/types/common";

import classes from "./CustomTable.module.css";
import { Box, Group, Pagination, Select, Stack, Text } from "@mantine/core";
import { MRT_LOCALIZATION_VI } from "constants/locales/mrt";
import { IconInboxOff } from "@tabler/icons-react";

interface Props<T extends Record<string, any>> {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  pagination?: PaginationMetadata;
  isLoading?: boolean;
  emptyMessage?: string;
  onStateChange?: (state: {
    page: number;
    limit: number;
    filters?: MRT_ColumnFiltersState;
  }) => void;
  mrtOptions?: Partial<MRT_TableOptions<T>>;
  /** Bật checkbox chọn dòng. Truyền hàm để chỉ cho phép chọn một số dòng nhất định. */
  enableRowSelection?: boolean | ((row: MRT_Row<T>) => boolean);
  getRowId?: (row: T, index: number) => string;
  /** Selection do component cha kiểm soát, dùng để giữ lựa chọn qua phân trang server. */
  rowSelection?: MRT_RowSelectionState;
  onRowSelectionChange?: (updater: MRT_Updater<MRT_RowSelectionState>) => void;
  selectAllMode?: "all" | "page";
}

const CustomTable = <T extends Record<string, any>>({
  columns,
  data,
  pagination,
  isLoading,
  emptyMessage = "Không có dữ liệu",
  onStateChange,
  mrtOptions,
  enableRowSelection = false,
  getRowId,
  rowSelection,
  onRowSelectionChange,
  selectAllMode = "page",
}: Props<T>) => {
  const tableColumns = useMemo(
    () =>
      columns.map((column) => {
        const getColumnMetaText = () =>
          [
            column.id,
            typeof column.header === "string" ? column.header : "",
            typeof column.accessorKey === "string" ? column.accessorKey : "",
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        const getAlignClassName = (align?: string) => {
          if (align === "center") return classes.alignCenter;
          if (align === "right") return classes.alignRight;
          return undefined;
        };

        const isIndexColumn =
          column.id === "stt" || column.header === "STT";

        const getInferredBodyAlign = () => {
          const columnMetaText = getColumnMetaText();

          if (isIndexColumn) return "center";
          if (
            column.id === "actions" ||
            columnMetaText.includes("thao tác") ||
            columnMetaText.includes("trạng thái") ||
            columnMetaText.includes("status")
          ) {
            return "center";
          }
          if (
            /amount|price|cost|revenue|profit|debt|paid|remaining|totalvalue/i.test(
              columnMetaText,
            ) ||
            /tổng tiền|số tiền|giá|giá net|đã thanh toán|đã thu|còn lại|doanh thu|lợi nhuận|công nợ|thực thu/i.test(
              columnMetaText,
            )
          ) {
            return "right";
          }

          return undefined;
        };

        const normalizeCellProps = (
          props: unknown,
          fallbackAlign?: "center" | "right",
        ) => {
          if (typeof props === "function") return props;

          const cellProps = props as {
            align?: string;
            className?: string;
            style?: Record<string, unknown>;
          } | undefined;
          const align = cellProps?.align ?? fallbackAlign;
          const alignClassName = getAlignClassName(align);

          if (!alignClassName) return props;

          return {
            ...cellProps,
            className: [cellProps?.className, alignClassName]
              .filter(Boolean)
              .join(" "),
            style: {
              ...cellProps?.style,
              textAlign: align,
              justifyContent:
                align === "right" ? "flex-end" : align,
            },
          };
        };

        return {
          ...column,
          Cell: isIndexColumn
            ? ({ row, table }: any) => {
                const { pageIndex, pageSize } = table.getState().pagination;
                return pageIndex * pageSize + row.index + 1;
              }
            : column.Cell,
          mantineTableHeadCellProps: normalizeCellProps(
            column.mantineTableHeadCellProps,
            "center",
          ) as typeof column.mantineTableHeadCellProps,
          mantineTableBodyCellProps: normalizeCellProps(
            column.mantineTableBodyCellProps,
            getInferredBodyAlign(),
          ) as typeof column.mantineTableBodyCellProps,
          mantineTableFooterCellProps: normalizeCellProps(
            column.mantineTableFooterCellProps,
            getInferredBodyAlign(),
          ) as typeof column.mantineTableFooterCellProps,
        };
      }),
    [columns],
  );
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: (pagination?.page ?? 1) - 1,
    pageSize: pagination?.limit ?? 10,
  });

  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  // Sync internal pagination state the first time pagination data arrives from API
  const paginationReady = useRef(!!pagination);
  useEffect(() => {
    if (pagination && !paginationReady.current) {
      paginationReady.current = true;
      setPaginationState({
        pageIndex: pagination.page - 1,
        pageSize: pagination.limit,
      });
    }
  }, [pagination]);

  useEffect(() => {
    onStateChangeRef.current?.({
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
      filters: columnFilters,
    });
  }, [paginationState.pageIndex, paginationState.pageSize, columnFilters]);

  const isPaginated = !!pagination;

  const table = useMantineReactTable({
    columns: tableColumns,
    data,
    rowCount: pagination?.total,
    enablePagination: isPaginated,
    enableColumnFilters: !!onStateChange,
    manualFiltering: !!onStateChange,
    manualPagination: !!onStateChange,
    layoutMode: "grid",
    enableColumnResizing: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnPinning: false,
    enableColumnActions: false,
    enableSorting: false,
    enableRowSelection,
    getRowId,
    selectAllMode,

    initialState: {
      density: "xs",
    },

    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPaginationState,
    onRowSelectionChange,

    state: {
      columnFilters,
      pagination: paginationState,
      isLoading,
      showProgressBars: isLoading,
      ...(rowSelection ? { rowSelection } : {}),
    },

    localization: MRT_LOCALIZATION_VI,
    renderEmptyRowsFallback: () => (
      <Stack align="center" justify="center" gap="xs" py="xl">
        <IconInboxOff size={40} color="var(--mantine-color-gray-5)" stroke={1.5} />
        <Text size="sm" c="dimmed">{emptyMessage}</Text>
      </Stack>
    ),
    renderTopToolbar: () => <></>,
    mantineTopToolbarProps: {},
    renderBottomToolbar: isPaginated
      ? ({ table }) => (
          <Box p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
            <Group justify="flex-end" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed">Số dòng hiển thị:</Text>
                <Select
                  data={["10", "25", "50", "100"]}
                  value={String(table.getState().pagination.pageSize)}
                  onChange={(val) => { table.setPageSize(Number(val)); }}
                  size="xs"
                  w={75}
                  radius="md"
                  variant="filled"
                />
              </Group>
              <Pagination
                total={table.getPageCount()}
                value={table.getState().pagination.pageIndex + 1}
                onChange={(page) => table.setPageIndex(page - 1)}
                size="sm"
                radius="md"
                withEdges
              />
            </Group>
          </Box>
        )
      : () => <></>,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
    },

    mantineTableContainerProps: {
      className: classes.tableContainer,
      style: { overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 250px)" },
    },

    mantineTableProps: {
      striped: true,
      withColumnBorders: true,
      withRowBorders: true,
    },
    paginationDisplayMode: "pages",
    ...mrtOptions,
  });

  return <MantineReactTable table={table} />;
};

export default memo(CustomTable) as typeof CustomTable;
